// Liste des signalements : à qualifier d'abord (filtres par qualification, catégorie, origine),
// numéro, auteur, service, intervention née ; dépôt d'un signalement par un agent (constat sur le
// terrain) ou au guichet pour un tiers.
import { CATEGORIES_SIGNALEMENT, LIBELLES_QUALIFICATION, QUALIFICATIONS_SIGNALEMENT, schemaCreationSignalementAgent } from '@cc/contrats';
import { Bandeau, Bouton, Champ, Dialogue, EtatVide, Filtres, Lien, LienBouton, PuceEtat, Selection, Tableau, useFiltresPersistes, ZoneTexte, type ColonneTableau } from '@cc/ui';
import { ArrowLeft, Megaphone, Plus } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { EnTetePage } from '../../commun/EnTetePage';
import { EtatRequete } from '../../commun/EtatRequete';
import { dateHeure } from '../../commun/format';
import { useAction } from '../../commun/useAction';
import { useSession } from '../../session/FournisseurSession';
import { peut } from '../../session/permissions';
import { creerSignalement, INVALIDER_INTERVENTIONS, useSignalements } from './api';
import { LIBELLES_AUTEUR, TON_QUALIFICATION } from './logique';
import type { Signalement } from './types';
import commun from '../../commun/commun.module.css';

export function puceQualification(s: Pick<Signalement, 'qualification' | 'qualification_libelle'>) {
  return <PuceEtat ton={TON_QUALIFICATION[s.qualification]} libelle={s.qualification_libelle} />;
}

function FormulaireSignalement({ ouvert, onFermer }: { ouvert: boolean; onFermer: () => void }) {
  const naviguer = useNavigate();
  const [v, setV] = useState({ categorie: '', description: '', adresse: '', auteur_type: 'agent', tiers_nom: '', tiers_telephone: '', longitude: '', latitude: '' });
  const [erreurs, setErreurs] = useState<Record<string, string>>({});
  const action = useAction({
    executer: () => {
      const position = v.longitude && v.latitude ? { longitude: Number(v.longitude), latitude: Number(v.latitude) } : undefined;
      const corps = schemaCreationSignalementAgent.safeParse({
        categorie: v.categorie,
        description: v.description,
        adresse: v.adresse || undefined,
        auteur_type: v.auteur_type,
        tiers_nom: v.tiers_nom || undefined,
        tiers_telephone: v.tiers_telephone || undefined,
        position,
      });
      if (!corps.success) {
        setErreurs(Object.fromEntries(corps.error.issues.map((i) => [String(i.path[0]), i.message])));
        return Promise.reject(new Error('Formulaire incomplet'));
      }
      setErreurs({});
      return creerSignalement(corps.data);
    },
    succes: (s) => `Signalement ${s.numero} enregistré.`,
    invalider: INVALIDER_INTERVENTIONS,
    apres: (s) => void naviguer(`/interventions/signalements/${s.id}`),
  });
  const champ = (nom: string) => action.erreursChamps.find((e) => e.champ === nom)?.message ?? erreurs[nom];
  return (
    <Dialogue
      ouvert={ouvert}
      titre="Nouveau signalement"
      description="Constat d'un agent sur le terrain ou signalement reçu au guichet pour un tiers. Une position ou une adresse est nécessaire."
      onFermer={onFermer}
      actions={
        <>
          <Bouton variante="secondaire" onClick={onFermer}>
            Annuler
          </Bouton>
          <Bouton variante="principal" chargement={action.enCours} onClick={() => action.lancer(undefined)}>
            Enregistrer
          </Bouton>
        </>
      }
    >
      <Selection libelle="Origine" options={[{ valeur: 'agent', libelle: 'Constat d\'un agent' }, { valeur: 'guichet', libelle: 'Guichet, pour un tiers' }]} value={v.auteur_type} onChange={(e) => setV({ ...v, auteur_type: e.target.value })} />
      <Selection libelle="Catégorie" optionVide="Choisir" options={Object.entries(CATEGORIES_SIGNALEMENT).map(([valeur, libelle]) => ({ valeur, libelle }))} value={v.categorie} onChange={(e) => setV({ ...v, categorie: e.target.value })} erreur={champ('categorie')} required />
      <ZoneTexte libelle="Description" value={v.description} onChange={(e) => setV({ ...v, description: e.target.value })} erreur={champ('description')} required rows={4} />
      <Champ libelle="Adresse ou lieu" value={v.adresse} onChange={(e) => setV({ ...v, adresse: e.target.value })} erreur={champ('adresse') ?? champ('position')} />
      <Champ libelle="Longitude" type="number" step="any" value={v.longitude} onChange={(e) => setV({ ...v, longitude: e.target.value })} facultatif />
      <Champ libelle="Latitude" type="number" step="any" value={v.latitude} onChange={(e) => setV({ ...v, latitude: e.target.value })} facultatif />
      {v.auteur_type === 'guichet' ? (
        <>
          <Champ libelle="Nom du tiers" value={v.tiers_nom} onChange={(e) => setV({ ...v, tiers_nom: e.target.value })} facultatif />
          <Champ libelle="Téléphone du tiers" value={v.tiers_telephone} onChange={(e) => setV({ ...v, tiers_telephone: e.target.value })} facultatif />
        </>
      ) : null}
      {action.messageErreur && action.messageErreur !== 'Formulaire incomplet' ? <Bandeau type="erreur">{action.messageErreur}</Bandeau> : null}
    </Dialogue>
  );
}

export function PageSignalements() {
  const { contexte } = useSession();
  const [filtres, setFiltres] = useFiltresPersistes('signalements', { qualification: 'a_qualifier', categorie: '', auteur_type: '' });
  const f = { qualification: filtres.qualification ?? '', categorie: filtres.categorie ?? '', auteur_type: filtres.auteur_type ?? '' };
  const liste = useSignalements(f);
  const [creation, setCreation] = useState(false);
  const colonnes: ColonneTableau<Signalement>[] = [
    { id: 'numero', entete: 'Numéro', enTeteLigne: true, masquable: false, valeurTri: (s) => s.numero, cellule: (s) => <Lien href={`/interventions/signalements/${s.id}`} className={commun.reference}>{s.numero}</Lien> },
    { id: 'categorie', entete: 'Catégorie', cellule: (s) => s.categorie_libelle, valeurTri: (s) => s.categorie_libelle },
    { id: 'description', entete: 'Description', cellule: (s) => (s.description.length > 90 ? `${s.description.slice(0, 90)}…` : s.description) },
    { id: 'lieu', entete: 'Lieu', cellule: (s) => s.adresse ?? (s.position ? `${s.position.latitude.toFixed(4)}, ${s.position.longitude.toFixed(4)}` : 'non renseigné') },
    { id: 'auteur', entete: 'Origine', cellule: (s) => `${LIBELLES_AUTEUR[s.auteur.type] ?? s.auteur.type}${s.auteur.nom ? ` · ${s.auteur.nom}` : ''}` },
    { id: 'recu', entete: 'Reçu le', cellule: (s) => dateHeure(s.cree_le), valeurTri: (s) => s.cree_le },
    { id: 'qualification', entete: 'Qualification', cellule: puceQualification, valeurTri: (s) => s.qualification },
    // Un dépôt sauvage arrive orienté vers le service de la propreté (D-044, D-131).
    { id: 'service', entete: 'Service', cellule: (s) => s.service?.nom ?? 'non désigné', valeurTri: (s) => s.service?.nom ?? '' },
    { id: 'intervention', entete: 'Intervention', cellule: (s) => (s.intervention ? <Lien href={`/interventions/${s.intervention.id}`}>{s.intervention.numero ?? 'voir'}</Lien> : s.principal ? `doublon de ${s.principal.numero ?? ''}` : 'aucune') },
  ];
  return (
    <>
      <EnTetePage
        etiquette="Interventions"
        titre="Signalements"
        description="Signalements du portail, des agents et du guichet. Le chef de service ou le responsable technique les qualifie : recevable (une intervention naît), orienté vers un organisme, rejeté ou doublon."
        actions={
          <>
            <LienBouton href="/interventions" icone={ArrowLeft}>
              Interventions
            </LienBouton>
            {peut(contexte, 'intervention', 'creer') ? (
              <Bouton variante="principal" icone={Plus} onClick={() => setCreation(true)}>
                Nouveau signalement
              </Bouton>
            ) : null}
          </>
        }
      />
      <Filtres
        libelle="Filtres des signalements"
        groupes={[
          { id: 'qualification', libelle: 'Qualification', options: [{ valeur: '', libelle: 'Toutes' }, ...QUALIFICATIONS_SIGNALEMENT.map((q) => ({ valeur: q, libelle: LIBELLES_QUALIFICATION[q] }))] },
          { id: 'categorie', libelle: 'Catégorie', options: [{ valeur: '', libelle: 'Toutes' }, ...Object.entries(CATEGORIES_SIGNALEMENT).map(([valeur, libelle]) => ({ valeur, libelle }))] },
          { id: 'auteur_type', libelle: 'Origine', options: [{ valeur: '', libelle: 'Toutes' }, ...Object.entries(LIBELLES_AUTEUR).map(([valeur, libelle]) => ({ valeur, libelle }))] },
        ]}
        valeurs={filtres}
        onChanger={setFiltres}
      />
      <EtatRequete chargement={liste.isPending} erreur={liste.error} onReessayer={() => void liste.refetch()} lot="L3-A" fonction="La liste des signalements">
        {(liste.data ?? []).length === 0 ? (
          <EtatVide titre="Aucun signalement" icone={Megaphone}>
            <p>Aucun signalement ne correspond à ces filtres.</p>
          </EtatVide>
        ) : (
          <Tableau legende="Signalements" colonnes={colonnes} lignes={liste.data ?? []} cleLigne={(s) => s.id} cleColonnes="signalements" />
        )}
      </EtatRequete>
      <FormulaireSignalement ouvert={creation} onFermer={() => setCreation(false)} />
    </>
  );
}
